import type { AppDispatch } from '../../app/store';
import { gridApi } from '../grid/api/gridApi';
import type { ColumnMetadata, LookupDataItem } from '../grid/types';
import {
  getProjectScopedLookupFieldType,
  getLookupMapKeys,
  isCompatibleLookupSample,
} from './lookupUtils';

type LookupResult = {
  map: Record<string, string>;
  array: LookupDataItem[];
  allMap?: Record<string, string>;
  allArray?: LookupDataItem[];
};

const inFlightLookups = new Map<string, Promise<LookupResult>>();

export type ProjectScopedAllowedIds = {
  releaseIds?: Array<number | string>;
  employeeIds?: Array<number | string>;
};

type LoadRelationLookupDataOptions = {
  dispatch: AppDispatch;
  fields: ColumnMetadata[];
  contentKey: string;
  mode: 'add' | 'edit';
  parentId?: string | number;
  entity?: Record<string, unknown>;
  initialValues?: Record<string, unknown>;
  projectScopedAllowedIds?: ProjectScopedAllowedIds;
  logPrefix: string;
};

type LoadRelationLookupDataResult = {
  maps: Record<string, Record<string, string>>;
  displayMaps: Record<string, Record<string, string>>;
  arrays: Record<string, LookupDataItem[]>;
};

const toAllowedIdSet = (ids?: Array<number | string>): Set<string> | undefined => {
  if (!ids) return undefined;

  const result = new Set<string>();
  for (const id of ids) {
    const normalized = String(id ?? '').trim();
    if (normalized) {
      result.add(normalized);
    }
  }

  return result;
};

const filterLookupResultByAllowedIds = (
  result: LookupResult,
  allowedIds: Set<string> | undefined,
  preserveId?: string | number
): LookupResult => {
  if (!allowedIds) return result;
  const displayAllowedIds = new Set(allowedIds);
  if (preserveId !== undefined && String(preserveId) !== '0' && String(preserveId) !== '') {
    displayAllowedIds.add(String(preserveId));
  }

  const filteredMap = Object.entries(result.map).reduce<Record<string, string>>(
    (acc, [id, label]) => {
      if (allowedIds.has(String(id))) {
        acc[id] = label;
      }
      return acc;
    },
    {}
  );

  const filteredArray = result.array.filter((item) => allowedIds.has(String(item.Id)));

  return {
    map: filteredMap,
    array: filteredArray,
    allMap: result.allMap
      ? Object.fromEntries(
          Object.entries(result.allMap).filter(([id]) => displayAllowedIds.has(String(id)))
        )
      : result.allMap,
    allArray: result.allArray?.filter((item) => displayAllowedIds.has(String(item.Id))),
  };
};

const LOOKUP_INFLIGHT_TIMEOUT_MS = 15000;

const getLookupResult = (
  dispatch: AppDispatch,
  params: Parameters<typeof gridApi.endpoints.getLookup.initiate>[0]
): Promise<LookupResult> => {
  const key = JSON.stringify(params);
  const existing = inFlightLookups.get(key);
  if (existing) return existing;

  const subscription = dispatch(gridApi.endpoints.getLookup.initiate(params, { subscribe: false }));
  const timeoutId = setTimeout(() => subscription.abort(), LOOKUP_INFLIGHT_TIMEOUT_MS);

  const request = subscription.unwrap().finally(() => clearTimeout(timeoutId));
  inFlightLookups.set(key, request);
  void request.then(
    () => {
      if (inFlightLookups.get(key) === request) inFlightLookups.delete(key);
    },
    () => {
      if (inFlightLookups.get(key) === request) inFlightLookups.delete(key);
    }
  );
  return request;
};

export const loadRelationLookupData = async ({
  dispatch,
  fields,
  contentKey,
  mode,
  parentId,
  entity,
  initialValues: _initialValues,
  projectScopedAllowedIds,
  logPrefix,
}: LoadRelationLookupDataOptions): Promise<LoadRelationLookupDataResult> => {
  const maps: Record<string, Record<string, string>> = {};
  const displayMaps: Record<string, Record<string, string>> = {};
  const arrays: Record<string, LookupDataItem[]> = {};
  const allowedReleaseIds = toAllowedIdSet(projectScopedAllowedIds?.releaseIds);
  const allowedEmployeeIds = toAllowedIdSet(projectScopedAllowedIds?.employeeIds);

  const relationFields = fields.filter(
    (field) =>
      (field.editor === 'selectgrid' || field.editor === 'selectlookup') && !field.dependsOn?.length
  );

  for (const field of relationFields) {
    if (!field.key || !field.dataField) continue;

    try {
      const currentFieldValue =
        mode === 'edit' && entity?.[field.dataField]
          ? (entity[field.dataField] as string | number)
          : 0;

      const result = await getLookupResult(dispatch, {
        nodeKey: contentKey,
        fieldKey: field.key,
        displayFieldName: field.additionalData?.displayExpr,
        defaultFilters: field.defaultFilters,
        objectId: currentFieldValue,
        parentId,
      });

      const lookupFieldType = getProjectScopedLookupFieldType(field);
      const filteredResult =
        lookupFieldType === 'release'
          ? filterLookupResultByAllowedIds(result, allowedReleaseIds, currentFieldValue)
          : lookupFieldType === 'employee'
            ? filterLookupResultByAllowedIds(result, allowedEmployeeIds, currentFieldValue)
            : result;

      const sampleFields =
        Array.isArray(filteredResult.array) && filteredResult.array.length > 0
          ? (filteredResult.array[0].Fields || []).map((item) => String(item.Name))
          : [];

      if (
        !isCompatibleLookupSample(sampleFields, field.dataField, field.additionalData?.displayExpr)
      ) {
        console.warn(
          `[${logPrefix}] skipping incompatible lookup for dataField=${field.dataField} (key=${field.key}) — sampleFields=${JSON.stringify(
            sampleFields
          )}`
        );
        continue;
      }

      for (const lookupKey of getLookupMapKeys(field)) {
        maps[lookupKey] = filteredResult.map;
        displayMaps[lookupKey] = filteredResult.allMap ?? filteredResult.map;
        arrays[lookupKey] = filteredResult.array;
      }
    } catch {
      // Lookup failures are non-fatal — the field renders without options.
    }
  }

  return { maps, displayMaps, arrays };
};
